/**
 * Password policy shared with backend/models/schemas.py password_strength_value.
 * Min 10 chars, at least one lowercase, uppercase, and digit.
 */

export const PASSWORD_POLICY_HINT =
  'At least 10 characters, with uppercase, lowercase, and a number';

export function validatePasswordParts(value, { required = true } = {}) {
  const password = String(value || '');
  if (!password) {
    if (required) return { valid: false, password: '', error: 'passwordRequired' };
    return { valid: true, password: '', error: null };
  }
  if (password.length < 10) {
    return { valid: false, password, error: 'passwordTooShort' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, password, error: 'passwordNeedsLower' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, password, error: 'passwordNeedsUpper' };
  }
  if (!/\d/.test(password)) {
    return { valid: false, password, error: 'passwordNeedsNumber' };
  }
  return { valid: true, password, error: null };
}

export function passwordErrorMessage(result, t) {
  if (!result?.error) return '';
  const key = `validation.${result.error}`;
  const msg = t ? t(key, { defaultValue: '' }) : '';
  if (msg && msg !== key) return msg;
  switch (result.error) {
    case 'passwordRequired':
      return 'Password is required.';
    case 'passwordTooShort':
      return 'Password must be at least 10 characters.';
    case 'passwordNeedsLower':
      return 'Password must include at least one lowercase letter (a–z).';
    case 'passwordNeedsUpper':
      return 'Password must include at least one uppercase letter (A–Z).';
    case 'passwordNeedsNumber':
      return 'Password must include at least one number (0–9).';
    default:
      return PASSWORD_POLICY_HINT;
  }
}

/** Clean FastAPI/Pydantic validation messages into readable copy. */
export function friendlyPasswordApiError(detailText) {
  const raw = String(detailText || '');
  const cleaned = raw
    .replace(/^Value error[,:]?\s*/i, '')
    .replace(/^body\.\w+\s*/i, '')
    .trim();
  if (/password/i.test(cleaned)) {
    if (/at least 10/i.test(cleaned)) return 'Password must be at least 10 characters.';
    if (/lowercase/i.test(cleaned)) return 'Password must include at least one lowercase letter (a–z).';
    if (/uppercase/i.test(cleaned)) return 'Password must include at least one uppercase letter (A–Z).';
    if (/number|digit/i.test(cleaned)) return 'Password must include at least one number (0–9).';
  }
  return cleaned || raw;
}
